import { BAND_CONFIG, WINDOW_SECONDS } from './config';
import type { AggregatedBandFeature, AggregationStats, BandFrameFeature, ScoredSituation, SituationKey } from './types';
import { autocorrelation, clamp01, getTrend, mean, pushLimited, slope, standardDeviation, variance } from './utils';

type GlobalFeatureKey =
  | 'loudness'
  | 'volatility'
  | 'entropy'
  | 'rhythmicity'
  | 'dominance'
  | 'diffuseness'
  | 'speechCoherence'
  | 'noiseIntensity';

type TemporalInput = Record<GlobalFeatureKey, number>;
type WindowId = keyof typeof WINDOW_SECONDS;

const MAX_HISTORY_SECONDS = WINDOW_SECONDS.long + 5;

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function getStats(values: number[]): AggregationStats {
  if (!values.length) {
    return { mean: 0, variance: 0, slope: 0, stability: 0, persistence: 0, autocorrelation: 0 };
  }
  const avg = mean(values);
  const varValue = variance(values);
  const stdValue = Math.sqrt(varValue);
  const stable = clamp01(1 - stdValue * 3);
  const persistence = values.filter((value) => value >= avg).length / values.length;
  return {
    mean: avg,
    variance: varValue,
    slope: slope(values),
    stability: stable,
    persistence: clamp01(persistence),
    autocorrelation: autocorrelation(values)
  };
}

export class TemporalAggregator {
  private frameDurationSec = 0.032;

  private readonly featureHistory: Record<GlobalFeatureKey, number[]> = {
    loudness: [],
    volatility: [],
    entropy: [],
    rhythmicity: [],
    dominance: [],
    diffuseness: [],
    speechCoherence: [],
    noiseIntensity: []
  };

  private readonly bandHistory: Record<string, number[]> = Object.fromEntries(
    BAND_CONFIG.map((band) => [band.key, []])
  );

  private readonly situationHistory: Record<SituationKey, number[]> = {
    single_speaker: [],
    conversation: [],
    babble: [],
    shouting: []
  };

  configure(frameDurationSec: number): void {
    if (frameDurationSec > 0) {
      this.frameDurationSec = frameDurationSec;
    }
  }

  reset(): void {
    (Object.keys(this.featureHistory) as GlobalFeatureKey[]).forEach((key) => {
      this.featureHistory[key] = [];
    });
    Object.keys(this.bandHistory).forEach((key) => {
      this.bandHistory[key] = [];
    });
    (Object.keys(this.situationHistory) as SituationKey[]).forEach((key) => {
      this.situationHistory[key] = [];
    });
  }

  push(features: TemporalInput, bands: BandFrameFeature[], situationScores: ScoredSituation[]): void {
    const maxPoints = Math.ceil(MAX_HISTORY_SECONDS / this.frameDurationSec);
    (Object.keys(features) as GlobalFeatureKey[]).forEach((key) => {
      this.featureHistory[key] = pushLimited(this.featureHistory[key], features[key], maxPoints);
    });
    bands.forEach((band) => {
      this.bandHistory[band.key] = pushLimited(this.bandHistory[band.key], band.energy, maxPoints);
    });
    situationScores.forEach((score) => {
      this.situationHistory[score.key] = pushLimited(this.situationHistory[score.key], score.score, maxPoints);
    });
  }

  private getWindowCount(windowId: WindowId): number {
    const seconds = WINDOW_SECONDS[windowId];
    return Math.max(1, Math.round(seconds / this.frameDurationSec));
  }

  getFeatureStats(feature: GlobalFeatureKey, windowId: WindowId): AggregationStats {
    const count = this.getWindowCount(windowId);
    return getStats(this.featureHistory[feature].slice(-count));
  }

  getSituationStats(key: SituationKey, windowId: WindowId): AggregationStats {
    const count = this.getWindowCount(windowId);
    return getStats(this.situationHistory[key].slice(-count));
  }

  getSituationStatsBySeconds(key: SituationKey, seconds: number): AggregationStats {
    const count = Math.max(1, Math.round(seconds / this.frameDurationSec));
    return getStats(this.situationHistory[key].slice(-count));
  }

  getBandAggregation(windowId: WindowId): AggregatedBandFeature[] {
    const count = this.getWindowCount(windowId);
    return BAND_CONFIG.map((band) => {
      const series = this.bandHistory[band.key].slice(-count);
      const stats = getStats(series);
      const threshold = median(series);
      const peakRate = series.length ? series.filter((value) => value > threshold * 1.25).length / series.length : 0;
      return {
        ...band,
        energy: stats.mean,
        meanEnergy: stats.mean,
        variance: stats.variance,
        volatility: clamp01(standardDeviation(series) * 3),
        trend: getTrend(stats.slope, 0.003),
        peakRate: clamp01(peakRate),
        persistence: stats.persistence
      };
    });
  }

  getCurrentSituationSlope(): number {
    const candidates = (Object.keys(this.situationHistory) as SituationKey[]).map((key) => {
      const stats = this.getSituationStats(key, 'short');
      return Math.abs(stats.slope);
    });
    return candidates.length ? mean(candidates) : 0;
  }
}
